import { useMutation, useQueryClient } from '@tanstack/react-query'
import { assert } from '@/utils'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assertIsDeliverTxSuccess, broadcastTx } from '@/wallet-utils'
import { MutationParameters } from './types'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { queryKeys } from '@/query-keys'
import { z } from 'zod'
import { convertAttributesToObject } from '../../utils'
import { getCachedChainBalances, useSelectedChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'
import { useSelectedDenom } from '../../../../contexts/denom'

export interface BroadcastTokenizeSharesMutationReturnType {
  tokenizedDenom: string
  tokenizedValue: string
}

const useBroadcastTokenizeSharesMutation = ({
  tokenizeSharesRaw,
  setTokenizeSharesDenom,
  setTokenizeSharesTokenValue
}: MutationParameters) => {
  const { address, getSigningStargateClient } = useSelectedChain()

  const [denom] = useSelectedDenom()

  const queryClient = useQueryClient()

  const handleMutation = async (): Promise<BroadcastTokenizeSharesMutationReturnType> => {
    assert(address, `Address is required for ${denom}.`)

    assert(tokenizeSharesRaw, 'Unable to broadcast transaction without a signed transaction raw.')

    const client = await getSigningStargateClient()

    const bytes = TxRaw.encode(tokenizeSharesRaw).finish()

    const tx = await broadcastTx(client, bytes)

    assertIsDeliverTxSuccess(tx)

    const { validator, share_record_id } = getTokenizedSharesAttributes(tx)

    // e.g., cosmosvaloper1uk4ze0x4nvh4fk0xm4jdud58eqn4yxhrdt795p/
    const tokenizedDenom = `${validator}/${share_record_id}`

    await queryClient.invalidateQueries({
      queryKey: queryKeys.chainBalancesBase
    })

    const selectedAccountBalances = getCachedChainBalances(queryClient, { denom, address })

    const tokenizedDenomBalance = selectedAccountBalances.balances.find((balance) => {
      return balance.denom === tokenizedDenom
    })

    assert(tokenizedDenomBalance, 'Missing tokenized denom balance. Fetched balance is probably stale.')

    return { tokenizedDenom, tokenizedValue: tokenizedDenomBalance.amount }
  }

  const handleSuccess = async ({ tokenizedDenom, tokenizedValue }: BroadcastTokenizeSharesMutationReturnType) => {
    // This is the only way to allow React v18 to update the state first before
    // the next mutation (which requires this state to have a value) is executed.
    flushSync(() => {
      setTokenizeSharesDenom(tokenizedDenom)
      setTokenizeSharesTokenValue(tokenizedValue)
    })

    // Make sure that the validators list is updated after the account balances have been updated.
    await queryClient.invalidateQueries({ queryKey: queryKeys.lsmValidatorsBase })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

const tokenizeSharesAttributesSchema = z.object({
  delegator: z.string(),
  validator: z.string(),
  share_owner: z.string(),
  share_record_id: z.string(),
  amount: z.string()
})

// We can get the denom given by `MsgTokenizeShares` with the format `{validator}/{shareRecordId}`
// e.g., cosmosvaloper1uk4ze0x4nvh4fk0xm4jdud58eqn4yxhrdt795p/1
// Ultimately, this function gets both data by parsing the `tokenize_shares` event from the transaction.
// Scanning for the `validator` and `share_record_id` attributes.
const getTokenizedSharesAttributes = (tx: DeliverTxResponse) => {
  const tokenizeSharesEvent = tx.events.find((event) => {
    return event.type === 'tokenize_shares'
  })

  assert(tokenizeSharesEvent, 'Missing `tokenize_shares` event from `MsgTokenizeShares` transaction.')

  return tokenizeSharesAttributesSchema.parse(convertAttributesToObject(tokenizeSharesEvent.attributes))
}

export { useBroadcastTokenizeSharesMutation }
